import { bindDataFactory } from './bindDataFactory';

/**
 * Factory para realizar el bind del scope para un registro dado en xPath.
 * Es para crear por ej. scope.LIN
 * Es para que funcione mgModel, las expressiones en visible, etc...
 *
 * @param {object} data - objeto encapsulando los datos, ej: "{CAB: {...}}"
 *
 * @return {function} que permite hacer el bind de los datos
 */
export function bindRegisterFactory(data) {
  /**
   * Añade el registro en el scope.
   * Si por ej. queremos CAB.LIN, va crear scope.LIN (por defecto buscara un objeto LIN, sino un array si pasamos type="array")
   *
   *
   * @param {object} scope el scope de angularjs, es el contexto que vamos a actualizar
   * @param {string} xPath el model completo del registro que buscamos, ej: 'CAB.LIN'
   * @param {object} {type}: tipo de registro que buscamos, por defecto "object", sino puede ser "array"
   */
  return function bindRegister(scope, xPath, { type = 'object' } = {}) {
    const models = xPath.split('.');
    const isMultiple = type === 'array';

    if (models.length === 1) {
      // OPTIMIZACION si el bind es de CAB directamente, ya esta hecho
      bindDataFactory(data)(scope);
      return;
    }

    /**
     * registers es un objeto {<model>: <registro PADRE>}
     * {
     *   'CAB': { CAB: {...} },
     *   'LIN': { LIN: {...} },
     * }
     */
    const registers = getRegisters(data, scope, models, isMultiple);

    // definimos los proxy
    Object.entries(registers).forEach(([model, parentRegister]) => {
      if (scope.hasOwnProperty(model)) {
        return;
      }
      Object.defineProperty(scope, model, {
        get: () => {
          return parentRegister[model];
        },
        set: value => {
          parentRegister[model] = value;
        },
        enumerable: true,
      });
    });
  };
}

/**
 *
 * @param {object} data los datos de la pantalla, ej: data = { CAB: {...} }
 * @param {object} scope el scope actual
 * @param {array} models la lista de registros que buscamos ej: "CAB.EMB.LIN" -> ['CAB', 'EMB', 'LIN']
 * @param {boolean} isMultiple indica si buscamos un array para el target (el ultimo del path)
 * @return {object} El objeto esde tipo {model: <el registro padre que contiene el model>}, ej:
 * {
 *   LIN: { LIN: {id: 1} },
 *   EMB: { EMB: { LIN: [{id: 1}] } },
 *   CAB: { CAB: { EMB: [ { LIN: [{id: 1}] } ] } }
 * }
 */
function getRegisters(data, scope, models, isMultiple) {
  const target = models.slice(-1)[0]; // CAB.LIN.ADJ -> ADJ
  const toFind = new Set(models);
  const registers = {};

  const checkIfFound = checkIfFoundFactory(registers, toFind, target, isMultiple);
  // 1 - Buscamos los registros en los datos (solo valido cuando son objetos)
  let cur = data;
  let i = 0;
  while (toFind.size && typeof cur === 'object') {
    const model = models[i];
    checkIfFound(cur, model);
    i++;
    cur = cur[model];
  }

  // 2 - Buscamos los registros en el scope (permite encontrar el registro corriente de un array)
  cur = scope;
  while (toFind.size && cur) {
    [...toFind].forEach(model => {
      checkIfFound(cur, model);
    });
    cur = cur.$parent;
  }

  // 3 - Creamos los registros que faltan
  let max = 10; // control de bucle infinito
  while (toFind.size && max--) {
    // ciertos registros no han sido encontrados... los creamos
    // pero debemos crearlos desde su padre mas proximo
    // para estar dentro de los registros de array que tocan
    [...toFind].forEach(model => {
      const parentModel = models[models.findIndex(m => m === model) - 1];
      // nota que hay que hacer register.LIN.LIN para recuperar el contenido de LIN
      // porque registers.LIN es el padre

      // miramos si existe el padre, sino buscaremos su padre etc...
      const register = registers[parentModel];
      if (register && register[parentModel]) {
        // recuperamos el objeto desde su padre o lo creamos en su padre para tener el binding
        register[parentModel][model] = register[parentModel][model] || {};
        // añadimos el nuevo registro a la lista de registers
        checkIfFound(register[parentModel], model);
      }
    });
  }

  return registers;
}

function checkIfFoundFactory(registers, toFind, target, isMultiple) {
  const isCorrectType = isCorrectTypeFactory(target, isMultiple);

  return (scope, model) => {
    if (scope.hasOwnProperty(model) && isCorrectType(scope, model)) {
      registers[model] = scope; // guaramos la referencia del scope (el padre, donde esta nuestro registro)
      toFind.delete(model); // quitamos el registro que ya encontramos de la lista a buscar
    }
  };
}

function isCorrectTypeFactory(target, isMultiple) {
  if (!isMultiple) {
    return (scope, model) => !Array.isArray(scope[model]);
  }
  return (scope, model) => {
    if (model === target) {
      return Array.isArray(scope[model]);
    }
    return !Array.isArray(scope[model]);
  };
}
